import { useState } from "react";

import { Button, Classes } from "@blueprintjs/core";
import styled from "@emotion/styled";

import { Box, Flex } from "@/components/layout/flexbox";
import { getUser } from "@/components/session/JwtTokenManager";
import { BP_NS, LIGHT_BACKGROUND } from "@/css/constants";
import { useClientNotesQuery } from "@/graphql";
import { CustomerUser } from "@/types/users";
import { ensureNonNullArray } from "@/utils/array";

import { Note } from "./Note";
import { ClientNoteEditable } from "./NoteEditable";

interface ClientNotesProps {
  user: CustomerUser;
  loading?: boolean;
}

export function ClientNotes({ user, loading: initLoading }: ClientNotesProps) {
  const currentUser = getUser();
  const [newNoteVisible, setNewNoteVisible] = useState(false);

  const { data, loading: queryLoading } = useClientNotesQuery({
    variables: {
      customerId: user?.id,
    },
    skip: initLoading || !user?.id,
  });

  const notes = ensureNonNullArray(data?.clientNotes);
  const loading = initLoading || queryLoading;

  const toggleNewNote = () => {
    setNewNoteVisible((x) => !x);
  };

  return (
    <Box mt={3}>
      <Box>
        {newNoteVisible ? (
          <ClientNoteEditable
            user={user}
            note={{
              id: "",
              content: "",
            }}
            onSaveCompleted={toggleNewNote}
            onCancel={toggleNewNote}
          />
        ) : (
          <StyledToggleButton onClick={toggleNewNote} rightIcon="plus">
            New Note
          </StyledToggleButton>
        )}
      </Box>
      <Flex flexDirection="column">
        {notes.map((note) => (
          <Note
            user={user}
            isAuthor={currentUser?.id === note.author.id}
            key={note.id}
            loading={loading}
            note={note}
          />
        ))}
      </Flex>
    </Box>
  );
}

const StyledToggleButton = styled(Button)`
  width: 100%;
  height: 56px;
  padding-right: 16px;
  &.${Classes.BUTTON}:not([class*='${BP_NS}-intent-']) {
    background-color: ${LIGHT_BACKGROUND};
    box-shadow: none;
    &:hover {
      box-shadow: none;
    }
  }
  .${Classes.BUTTON_TEXT} {
    flex-grow: 1;
    font-weight: 600;
  }
  &.${Classes.BUTTON}:not([class*='${BP_NS}-intent-'])
    .${Classes.ICON}:last-child {
    margin-right: 0;
  }
`;
